import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import cl from 'classnames';
import { HeadsetUserEvent } from 'src/models';
import { $TSFixMe } from 'src/interface';
import { HEADSETS } from 'src/constants';
import { formatDatetime } from 'src/helper';
import { useContextData, useLanguage, usePage } from 'src/hooks';
import { useAppDispatch } from 'src/store';
import {
  useHeadsetById,
  fetchHeadsetUserEventsAction,
} from 'src/services/headsets';
import {
  PageSwitcher,
  StickyTable,
  useStickyCols,
  StickyTableConfig,
} from 'src/components';
import {
  FilterModal,
  FilterModalButton,
  Tooltip,
} from 'src/components/general';
import styles from './headsetUserEventsStyle.module.scss';

const ActionCell: React.FC<{ value: $TSFixMe }> = ({ value }) => {
  const [useTooltip, setUseTooltip] = useState(false);

  return (
    <Tooltip
      text={JSON.stringify(value, undefined, 4)}
      tooltipClassName={styles.tooltip}
      arrowClassName={styles.tooltipArrow}
      withClipboard
      disabled={!useTooltip}
    >
      <div
        className={styles.action}
        ref={(node) => {
          if (node && node.getBoundingClientRect().width > 299) {
            setUseTooltip(true);
          }
        }}
      >
        {JSON.stringify(value)}
      </div>
    </Tooltip>
  );
};

export const HeadsetUserEvents = () => {
  const { hotel: companyId, headset: headsetId } = useParams() as {
    hotel: string;
    headset: string;
  };
  const {
    access: { accessMap },
  } = usePage();
  const {
    header: { width: headerWidth },
  } = useContextData();
  const dispatch = useAppDispatch();
  const { t } = useLanguage();
  const headset = useHeadsetById(headsetId);
  const [isLoading, setIsLoading] = useState<null | boolean>(null);
  const [showFiltersModal, setShowFiltersModal] = useState(false);

  const headsetIsLoaded = useMemo(() => Boolean(headset), [headset]);
  const userEvents = useMemo(
    () => headset?.userEvents || [],
    [headset?.userEvents],
  );

  useEffect(() => {
    if (!headsetIsLoaded) {
      return;
    }

    setIsLoading(true);

    dispatch(fetchHeadsetUserEventsAction({ companyId, headsetId })).then(
      () => {
        setIsLoading(false);
      },
    );
  }, [dispatch, headsetIsLoaded, headsetId, companyId]);

  const config: StickyTableConfig<HeadsetUserEvent>[] = useMemo(
    () => [
      {
        title: t('page.headset.userEvents.eventType'),
        key: 'eventType',
        field: 'eventType',
        render: (value, event) => (
          <Tooltip
            text={`ID: ${event.id}`}
            tooltipClassName={styles.tooltip}
            arrowClassName={styles.tooltipArrow}
            withClipboard
          >
            {value}
          </Tooltip>
        ),
      },
      {
        title: t('page.headset.userEvents.email'),
        key: 'email',
        field: 'email',
        render: (value, event) => (
          <Tooltip
            text={`ID: ${event.accountId}`}
            tooltipClassName={styles.tooltip}
            arrowClassName={styles.tooltipArrow}
            withClipboard
          >
            {value}
          </Tooltip>
        ),
      },
      {
        title: t('page.headset.userEvents.action'),
        key: 'action',
        field: 'action',
        render: (value) => <ActionCell value={value} />,
      },
      {
        title: t('page.headset.userEvents.createAt'),
        key: 'createAt',
        field: 'createAt',
        render: (value) => (
          <div className={styles.tag}>{formatDatetime(value)}</div>
        ),
      },
    ],
    [t],
  );

  const {
    stickyCols,
    stickyColsFilter,
    modalStickyColsFilter,
    applySCChanges,
    resetSCChanges,
  } = useStickyCols({
    config,
    className: styles.stickyCols,
    modalClassName: styles.modalStickyCols,
  });

  return (
    <div className={styles.container}>
      <FilterModal
        show={showFiltersModal}
        close={() => {
          setShowFiltersModal(false);
        }}
        onApply={() => {
          applySCChanges();
          setShowFiltersModal(false);
        }}
        onCancel={() => {
          resetSCChanges();
        }}
      >
        {modalStickyColsFilter}
      </FilterModal>
      <div
        className={styles.topSection}
        style={{ minWidth: headerWidth, maxWidth: headerWidth }}
      >
        <div
          className={cl(
            styles.pageSwitcherContainer,
            accessMap[HEADSETS].length === 1 && styles.inactive,
          )}
        >
          <PageSwitcher
            className={styles.pageSwitcher}
            selectClassName={styles.mobileSelect}
          />
          <div className={styles.filterButtons}>
            <FilterModalButton setShow={setShowFiltersModal} />
          </div>
        </div>
        <div className={styles.filters}>{stickyColsFilter}</div>
      </div>
      <StickyTable
        config={config}
        data={userEvents}
        withPaginator
        isLoading={isLoading}
        stickyCols={stickyCols}
        className={styles.table}
      />
    </div>
  );
};

export default HeadsetUserEvents;
